import { defineStore } from "pinia";
import { ref } from "vue";
import { authRepository } from "../../data/repositories/authRepository";

export interface SavedAccount {
  userId: string;
  name: string;
  email: string;
  avatarUrl?: string;
  ssoSessionToken?: string;
}

const SAVED_ACCOUNTS_KEY = "sso_saved_accounts";

export const useLoginStore = defineStore("loginStore", () => {
  const isLoading = ref<boolean>(true);
  const errorMessage = ref<string | null>(null);
  const clientName = ref<string>("");
  const clientDescription = ref<string>("");
  const clientLogo = ref<string | null>(null);
  const isClientValid = ref<boolean>(false);
  const savedAccounts = ref<SavedAccount[]>([]);

  function loadSavedAccounts() {
    const data = localStorage.getItem(SAVED_ACCOUNTS_KEY);
    if (data) {
      try {
        savedAccounts.value = JSON.parse(data);
      } catch {
        savedAccounts.value = [];
      }
    } else {
      savedAccounts.value = [];
    }
  }

  function saveAccount(account: SavedAccount) {
    // Hindari duplikasi: hapus akun lama jika ID-nya sama, lalu taruh akun baru di urutan teratas
    const filtered = savedAccounts.value.filter((acc) => acc.userId !== account.userId);
    savedAccounts.value = [account, ...filtered];
    localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(savedAccounts.value));
  }

  function removeAccount(userId: string) {
    savedAccounts.value = savedAccounts.value.filter((acc) => acc.userId !== userId);
    localStorage.setItem(SAVED_ACCOUNTS_KEY, JSON.stringify(savedAccounts.value));
  }

  async function validateClient(clientId: string, redirectUri: string) {
    isLoading.value = true;
    errorMessage.value = null;

    try {
      const response = await authRepository.validateClient(clientId, redirectUri);

      isClientValid.value = response.isValid;
      clientName.value = response.client.name;
      clientDescription.value = response.client.description ?? "";
      clientLogo.value = response.client.logo || null;
    } catch (error: any) {
      isClientValid.value = false;
      errorMessage.value = error.response?.data?.message || "Gagal memvalidasi klien atau Redirect URI tidak cocok.";
    } finally {
      isLoading.value = false;
    }
  }

  return {
    isLoading,
    errorMessage,
    clientName,
    clientDescription,
    clientLogo,
    isClientValid,
    savedAccounts,
    loadSavedAccounts,
    saveAccount,
    removeAccount,
    validateClient,
  };
});
